import React, { useState, useEffect } from 'react';
import { Modal, Typography, Spin, App, Space, Card, Button, Input, List, Row, Col, InputNumber } from 'antd';
import { PlusOutlined, MinusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { salesService } from '../services/api';
import {v4 as uuidv4} from 'uuid';

const { Title, Text } = Typography;

const SaleReturnModal = ({ visible, onClose, saleId, cashRegisterId, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1); // 1 - выбор товаров, 2 - выбор оплат
    const [prepareData, setPrepareData] = useState(null);
    const [selectedItems, setSelectedItems] = useState({});
    const [selectedPayments, setSelectedPayments] = useState({});
    const [activePaymentInput, setActivePaymentInput] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [initialAutoFillDone, setInitialAutoFillDone] = useState(false);
    const { message: messageApi } = App.useApp();

    // Загрузка данных prepare
    const loadPrepareData = async () => {
        setLoading(true);
        try {
            const response = await salesService.getReturnPrepare(saleId);
            if (response.status === 'success') {
                setPrepareData(response.data);
                // Инициализируем выбранные товары нулями
                const initialItems = {};
                response.data.available_items.forEach(item => {
                    initialItems[item.id] = 0;
                });
                setSelectedItems(initialItems);

                // Инициализируем выбранные оплаты нулями
                const initialPayments = {};
                response.data.available_payments.forEach(payment => {
                    initialPayments[payment.payment_method_id] = '';
                });
                setSelectedPayments(initialPayments);
            } else {
                messageApi.error(response.message || 'Ошибка при получении данных для возврата');
            }
        } catch (error) {
            console.error('Ошибка загрузки данных prepare:', error);
            messageApi.error('Не удалось загрузить данные для возврата');
        } finally {
            setLoading(false);
        }
    };

    // Загрузка данных при открытии модала
    useEffect(() => {
        if (visible && saleId) {
            loadPrepareData();
        }
    }, [visible, saleId]);

    // useEffect для ОДНОКРАТНОГО автоматического заполнения способов оплаты
    useEffect(() => {
        if (prepareData && step === 2 && !initialAutoFillDone) {
            const itemsTotal = calculateSelectedItemsTotal();

            // Автоматически заполняем только если есть товары для возврата
            if (itemsTotal > 0 && prepareData.available_payments.length > 0) {
                const firstPayment = prepareData.available_payments[0];
                const firstPaymentId = firstPayment.payment_method_id.toString();
                const amountToFill = Math.min(itemsTotal, firstPayment.available_amount);
                setSelectedPayments(prev => ({
                    ...prev,
                    [firstPaymentId]: amountToFill.toString()
                }));
            }
            setInitialAutoFillDone(true);
        }
    }, [step, prepareData, initialAutoFillDone]);

    // Сброс данных при закрытии модала
    const handleModalClose = () => {
        setPrepareData(null);
        setStep(1);
        setSelectedItems({});
        setSelectedPayments({});
        setActivePaymentInput(null);
        setInitialAutoFillDone(false);
        onClose();
    };

    // Изменение количества товара
    const handleItemQuantityChange = (itemId, change) => {
        setSelectedItems(prev => {
            const item = prepareData.available_items.find(i => i.id === itemId);
            const currentQuantity = prev[itemId] || 0;
            const newQuantity = Math.max(0, Math.min(item.available_quantity, currentQuantity + change));
            return {
                ...prev,
                [itemId]: newQuantity
            };
        });
    };

    // Переход к следующему шагу
    const handleNextStep = () => {
        const hasSelectedItems = Object.values(selectedItems).some(qty => qty > 0);
        if (!hasSelectedItems) {
            messageApi.error('Выберите хотя бы один товар для возврата');
            return;
        }
        setStep(2);
    };

    // Форматирование суммы
    const formatAmount = (amount) => {
        if (!amount) return '0.00';
        return new Intl.NumberFormat('ru-RU', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    // Создание возврата
    const handleCreateReturn = async () => {
        // Подготовка данных для возврата
        const returnItems = Object.entries(selectedItems)
            .filter(([_, quantity]) => quantity > 0)
            .map(([itemId, quantity]) => ({
                id: itemId,
                quantity: quantity
            }));

        const returnPayments = Object.entries(selectedPayments)
            .filter(([_, amount]) => amount && parseFloat(amount) > 0)
            .map(([paymentMethodId, amount]) => {
                const payment = prepareData.available_payments.find(p => p.payment_method_id.toString() === paymentMethodId);
                return {
                    type: payment.payment_type,
                    payment_method_id: parseInt(paymentMethodId),
                    sum: parseFloat(amount),
                    payment_details: null
                };
            });

        if (returnItems.length === 0) {
            messageApi.error('Выберите товары для возврата');
            return;
        }

        // Разрешаем создание возврата даже без указания суммы оплаты
        const returnData = {
            return_id: saleId,
            items: returnItems,
            payments: returnPayments,
            cash_register_id: cashRegisterId
        };


        const correlationKey = uuidv4();
        const idempotencyKey = uuidv4();

        setActionLoading(true);
        try {
            const response = await salesService.createReturn(returnData, correlationKey, idempotencyKey);
            if (response.status === 'success') {
                messageApi.success('Возврат успешно создан');
                if (onSuccess) onSuccess();
                handleModalClose();
            } else {
                messageApi.error(response.message || 'Ошибка при создании возврата');
            }
        } catch (error) {
            console.error('Ошибка при создании возврата:', error);
            messageApi.error('Не удалось создать возврат');
        } finally {
            setActionLoading(false);
        }
    };

    // Функция расчета общей суммы выбранных товаров
    const calculateSelectedItemsTotal = () => {
        if (!prepareData) return 0;

        return Object.entries(selectedItems).reduce((total, [itemId, quantity]) => {
            const item = prepareData.available_items.find(i => i.id == itemId);
            return total + (item ? parseFloat(item.unit_price) * quantity : 0);
        }, 0);
    };

    // Функция расчета общей суммы введенных оплат
    const calculateSelectedPaymentsTotal = () => {
        return Object.values(selectedPayments).reduce((total, amount) => {
            return total + (parseFloat(amount) || 0);
        }, 0);
    };

    // Функция обработки изменения суммы оплаты (БЕЗ автоматического пересчета)
    const handlePaymentInputChange = (paymentMethodId, value) => {
        const numValue = parseFloat(value) || 0;
        const payment = prepareData.available_payments.find(p => p.payment_method_id.toString() === paymentMethodId);

        // Проверка лимита
        if (numValue > payment.available_amount) {
            value = payment.available_amount.toString();
        }

        // Просто обновляем значение без дополнительной логики
        setSelectedPayments(prev => ({
            ...prev,
            [paymentMethodId]: value
        }));
    };

    // Рендер шага выбора товаров
    const renderItemsStep = () => (
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Text>Выберите товары для возврата:</Text>
                <List
                    dataSource={prepareData.available_items}
                    renderItem={item => (
                        <List.Item style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(6.25rem, 6fr) minmax(5.625rem, auto) minmax(5rem, auto)',
                                gridTemplateAreas: '"col-2 col-3 col-4"',
                                gap: '16px',
                                width: '100%',
                                alignItems: 'start'
                            }}>
                                {/* Название товара */}
                                <div style={{
                                    gridArea: 'col-2',
                                    wordWrap: 'break-word',
                                    wordBreak: 'break-word',
                                    lineHeight: '1.4',
                                    fontSize: '14px'
                                }}>
                                    {item.name}
                                </div>

                                {/* Инпут с количеством */}
                                <div style={{
                                    gridArea: 'col-3',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start'
                                }}>
                                    <InputNumber
                                        min={0}
                                        max={item.available_quantity}
                                        value={selectedItems[item.id] || 0}
                                        onChange={(value) =>
                                            setSelectedItems(prev => ({ ...prev, [item.id]: value || 0 }))
                                        }
                                        addonAfter={`/ ${item.available_quantity}`}
                                        controls={false}
                                        inputMode="numeric"
                                        style={{width: '90px'}}
                                    />
                                </div>

                                {/* Цена */}
                                <div style={{
                                    gridArea: 'col-4',
                                    textAlign: 'right',
                                    fontWeight: '500',
                                    fontSize: '14px',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {formatAmount(item.unit_price)} ₸
                                </div>
                            </div>
                        </List.Item>
                    )}
                />
            </Space>
        </div>
    );

    // Рендер шага выбора оплат
    const renderPaymentsStep = () => {
        const itemsTotal = calculateSelectedItemsTotal();
        const paymentsTotal = calculateSelectedPaymentsTotal();
        const remainder = itemsTotal - paymentsTotal;

        return (
            <div style={{ height: '518px', overflowY: 'clip' }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div>
                        <div>
                            <Row justify="space-between" align="middle">
                                <Col span={6}>
                                    <Text strong>Сумма</Text>
                                </Col>
                                <Col>
                                    <Text strong>{formatAmount(paymentsTotal)} ₸</Text>
                                </Col>
                            </Row>
                            <Row justify="space-between" align="middle">
                                <Col span={6}>
                                    <Text strong>Введено</Text>
                                </Col>
                                <Col>
                                    <Text strong>{formatAmount(itemsTotal)} ₸</Text>
                                </Col>
                            </Row>
                            <Row justify="space-between" align="middle">
                                <Col span={6}>
                                    <Text strong>Остаток</Text>
                                </Col>
                                <Col>
                                    <Text strong>{formatAmount(Math.abs(remainder))} ₸</Text>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        {prepareData.available_payments.map(payment => (
                            <Card key={payment.payment_method_id} size="small">
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <Row justify="space-between" align="middle">
                                        <Text strong>{payment.payment_method_name}</Text>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            Макс. {formatAmount(payment.available_amount)} ₸
                                        </Text>
                                    </Row>
                                    <Input
                                        size="large"
                                        placeholder="0.00"
                                        value={selectedPayments[payment.payment_method_id] || ''}
                                        onChange={(e) => handlePaymentInputChange(payment.payment_method_id.toString(), e.target.value)}
                                        onFocus={() => setActivePaymentInput(payment.payment_method_id)}
                                        suffix="₸"
                                        style={{ fontSize: '18px', textAlign: 'center' }}
                                    />
                                </Space>
                            </Card>
                        ))}
                    </Space>
                </Space>
            </div>
        );
    };

    // Рендер содержимого модала
    const renderContent = () => {
        if (loading) {
            return (
                <Space
                    direction="vertical"
                    size="large"
                    style={{ width: '100%', textAlign: 'center', padding: '40px 0' }}
                >
                    <Spin size="large" />
                </Space>
            );
        }

        if (!prepareData) {
            return (
                <Space
                    direction="vertical"
                    size="large"
                    style={{ width: '100%', textAlign: 'center', padding: '40px 0' }}
                >
                    <Text type="secondary">Нет данных для возврата</Text>
                </Space>
            );
        }

        return step === 1 ? renderItemsStep() : renderPaymentsStep();
    };

    return (
        <Modal
            title="Возврат товаров"
            open={visible}
            onCancel={handleModalClose}
            width={500}
            centered
            footer={
                step === 1 ? (
                    <Button
                        type="primary"
                        size="large"
                        onClick={handleNextStep}
                        style={{
                            width: '100%'
                        }}
                    >
                        Продолжить
                    </Button>
                ) : (
                    <Row gutter={8}>
                        <Col span={4}>
                            <Button
                                size="large"
                                onClick={() => setStep(1)}
                                icon={<ArrowLeftOutlined />}
                                style={{
                                    width: '100%'
                                }}
                            />
                        </Col>
                        <Col span={20}>
                            <Button
                                type="primary"
                                size="large"
                                onClick={handleCreateReturn}
                                loading={actionLoading}
                                style={{
                                    width: '100%'
                                }}
                            >
                                Завершить
                            </Button>
                        </Col>
                    </Row>
                )
            }
        >
            {renderContent()}
        </Modal>
    );
};

export default SaleReturnModal;